import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Smartphone, Battery, Droplets, Usb, ScanFace, Camera, Volume2,
  Laptop, Gamepad2, HardDrive, Wrench, ArrowRight, Phone, Clock,
  ShieldCheck, AlertTriangle,
} from "lucide-react";
import { SEO } from "@/components/SEO";
import { PANNES } from "@/data/pannesData";

const ICON_MAP = {
  Smartphone, Battery, Droplets, Usb, ScanFace, Camera, Volume2,
  Laptop, Gamepad2, HardDrive,
};

// Catégories pour le filtrage visuel
const CATEGORIES = ["iPhone", "Mac", "Urgence", "Console", "Informatique"];

const PHONE_LINK = "tel:+33549057490";

export default function PannesIndexPage() {
  // ItemList JSON-LD - aide les LLMs et Google à comprendre la structure
  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Pannes & réparations Mediastore Niort",
    description: "Catalogue complet des pannes traitées par Mediastore Niort : écran cassé iPhone, batterie, désoxydation, Face ID, caméra, Mac, PS5 HDMI, récupération de données.",
    numberOfItems: PANNES.length,
    itemListElement: PANNES.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `https://www.mediastore79.com/${p.slug}`,
      name: p.titleH1,
    })),
  };

  return (
    <div className="min-h-screen" data-testid="pannes-index-page">
      <SEO
        title="Pannes & Réparations - Tous nos services à Niort | Mediastore"
        description="Catalogue complet des pannes traitées par Mediastore Niort : écran cassé iPhone, batterie HS, désoxydation, Face ID, caméra, connecteur de charge, MacBook, PS5 HDMI, récupération de données. Diagnostic gratuit, garantie 6 mois. ☎ 05 49 05 74 90"
        keywords="pannes téléphone Niort, réparation écran iPhone Niort, batterie iPhone Niort, désoxydation Niort, Face ID Niort, caméra iPhone Niort, connecteur charge iPhone Niort, haut-parleur iPhone Niort, vitre arrière iPhone Niort, écran MacBook Niort, batterie MacBook Niort, PS5 HDMI Niort, récupération données Niort, Mediastore Niort pannes"
        path="/pannes-reparations"
        schema={itemListSchema}
        breadcrumbs={[
          { name: "Accueil", path: "/" },
          { name: "Pannes & Réparations", path: "/pannes-reparations" },
        ]}
      />

      {/* HERO */}
      <section className="py-16 bg-gradient-to-br from-slate-900 to-red-900 text-white">
        <div className="container-custom text-center">
          <Badge className="bg-white/10 text-white border border-white/20 px-4 py-2 mb-6 hover:bg-white/10">
            <Wrench className="w-4 h-4 mr-1" />
            {PANNES.length} pannes traitées à Niort
          </Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Toutes les pannes & réparations
          </h1>
          <p className="text-lg md:text-xl text-slate-200 max-w-3xl mx-auto leading-relaxed">
            Trouvez votre panne et découvrez la solution Mediastore : tarifs, délais et FAQ.
            <strong className="text-white"> Diagnostic gratuit</strong>, garantie 6 mois sur pièces
            et main-d&apos;œuvre.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <a href={PHONE_LINK}>
              <Button size="lg" className="bg-red-600 hover:bg-red-700 h-14 px-8 gap-2 text-lg font-semibold" data-testid="pannes-call-btn">
                <Phone className="h-5 w-5" />
                05 49 05 74 90
              </Button>
            </a>
            <Link to="/rendez-vous">
              <Button size="lg" variant="outline" className="h-14 px-8 gap-2 border-2 border-white/30 text-white hover:bg-white/10">
                Prendre rendez-vous
                <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Grid des pannes groupées par catégorie */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom max-w-6xl">
          {CATEGORIES.map((category) => {
            const pannesInCategory = PANNES.filter((p) => p.category === category);
            if (pannesInCategory.length === 0) return null;

            return (
              <div key={category} className="mb-12 last:mb-0">
                <div className="flex items-center gap-3 mb-6">
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
                    {category === "Urgence" ? "Urgences" : category}
                  </h2>
                  <span className="bg-red-100 text-red-700 text-sm font-semibold px-3 py-1 rounded-full">
                    {pannesInCategory.length} pannes
                  </span>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {pannesInCategory.map((panne) => {
                    const Icon = ICON_MAP[panne.icon] || Wrench;
                    const isUrgent = panne.category === "Urgence";

                    return (
                      <Link
                        key={panne.slug}
                        to={`/${panne.slug}`}
                        className="block group"
                        data-testid={`panne-link-${panne.slug}`}
                      >
                        <Card className={`h-full hover:shadow-xl transition-all duration-300 border-2 ${isUrgent ? "border-amber-200 bg-amber-50 hover:border-amber-400" : "border-slate-100 hover:border-red-200"}`}>
                          <CardContent className="p-5">
                            <div className="flex items-start justify-between mb-3">
                              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isUrgent ? "bg-amber-100" : "bg-red-50"}`}>
                                <Icon className={`h-6 w-6 ${isUrgent ? "text-amber-600" : "text-red-600"}`} />
                              </div>
                              {isUrgent && (
                                <AlertTriangle className="h-5 w-5 text-amber-600" />
                              )}
                            </div>

                            <h3 className="font-bold text-base md:text-lg text-slate-900 group-hover:text-red-600 transition-colors mb-2 leading-snug">
                              {panne.titleH1.split(" à Niort")[0].split(" - ")[0]}
                            </h3>

                            <div className="flex items-center gap-3 text-sm text-slate-600 mb-3">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5" />
                                {panne.durationLabel}
                              </span>
                              <span className="font-bold text-red-600">dès {panne.priceFrom} €</span>
                            </div>

                            <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                              <span className="flex items-center gap-1 text-xs text-slate-500">
                                <ShieldCheck className="h-3.5 w-3.5" />
                                Garantie 6 mois
                              </span>
                              <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-red-600 group-hover:translate-x-1 transition-all" />
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-12 bg-red-600 text-white">
        <div className="container-custom text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Votre panne n&apos;est pas listée ?
          </h2>
          <p className="text-red-100 mb-6 max-w-2xl mx-auto">
            Mediastore Niort répare également tous types d&apos;ordinateurs, tablettes, consoles
            et appareils Apple. Diagnostic gratuit, devis transparent.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={PHONE_LINK}>
              <Button size="lg" className="bg-white text-red-600 hover:bg-red-50 gap-2">
                <Phone className="h-5 w-5" />
                05 49 05 74 90
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10">
                Demander un devis
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
